import {useCallback, useMemo} from 'react'

import type {Iteration} from '../../api/columns/contracts/iteration'
import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {getAllIterationsForConfiguration, intervalDatesDescription, isCurrentIteration} from '../../helpers/iterations'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useDeleteGroup} from '../../hooks/use-delete-group'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import type {ColumnModel} from '../../models/column-model'
import {isIterationColumnModel} from '../../models/column-model/guards'
import type {Group} from '../../models/group'
import type {HorizontalGroup, ItemsGroupedByVerticalColumn} from '../../models/horizontal-group'
import type {MemexItemModel} from '../../models/memex-item-model'
import {MissingVerticalGroupId, type VerticalGroup} from '../../models/vertical-group'
import {BoardPagination} from './board-pagination'
import {Column, type ColumnHeaderType} from './column/column'
import {useUpdateBoardColumnDetails} from './hooks/use-update-board-column-details'

const defaultItemsGroupedByFieldPerOption: ReadonlyArray<MemexItemModel> = []
export function Columns({
  groupByFieldOptions,
  itemsGroupedByField,
  groupByField,
  scrollToItemId,
  lastNewColumnNameRef,
  horizontalGroup,
  horizontalGroupIndex,
  headerType = 'visible',
  hideColumn,
  isLastGroup,
}: {
  groupByFieldOptions: ReadonlyArray<Group>
  itemsGroupedByField: ItemsGroupedByVerticalColumn
  groupByField?: ColumnModel
  scrollToItemId?: number
  lastNewColumnNameRef: React.RefObject<string>
  horizontalGroup?: HorizontalGroup
  headerType?: ColumnHeaderType
  hideColumn: (option: Group) => boolean
  isLastGroup?: boolean
  horizontalGroupIndex: number
}) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {deleteGroup} = useDeleteGroup(groupByField)
  const {updateColumnDetails} = useUpdateBoardColumnDetails(groupByField)
  const {memex_table_without_limits} = useEnabledFeatures()

  /**
   * Returns whether the current `verticalGroup` corresponds to
   * an `Iteration` data type whose value is equal to the "current"
   * iteration - i.e. an iteration that is in progress date-wise
   */
  const isVerticalGroupCurrentIteration = useCallback(
    (verticalGroup: VerticalGroup): boolean => {
      if (!groupByField) return false

      if (groupByField.dataType !== MemexColumnDataType.Iteration) return false

      const iterationForVerticalGroup = groupByField.settings.configuration.iterations.find(
        (iteration: Iteration) => iteration.id === verticalGroup.id,
      )
      if (!iterationForVerticalGroup) return false
      return isCurrentIteration(new Date(), iterationForVerticalGroup)
    },
    [groupByField],
  )

  // We use this method to get the dates from each iteration in a map with their id
  const iterationDateRangeMap = useMemo(() => {
    const configuration = isIterationColumnModel(groupByField) ? groupByField.settings.configuration : null
    if (!configuration) {
      return new Map<string, string>()
    }

    const allIterations = getAllIterationsForConfiguration(configuration)
    const iterationDatesMap = new Map<string, string>()

    for (const iteration of allIterations) {
      iterationDatesMap.set(iteration.id, intervalDatesDescription(iteration))
    }

    return iterationDatesMap
  }, [groupByField])

  return (
    <>
      {groupByFieldOptions.map((option, index) => {
        const group = itemsGroupedByField[option.id]
        const filteredItemsFromField = group?.items ?? defaultItemsGroupedByFieldPerOption
        const isEditable = hasWritePermissions && option.id !== MissingVerticalGroupId
        const isDraggable = groupByField?.dataType !== MemexColumnDataType.Iteration

        return (
          <Column
            key={option.id}
            hidden={hideColumn(option)}
            option={option}
            groupedColumnId={group?.groupedColumnId}
            items={filteredItemsFromField}
            index={index}
            onDelete={deleteGroup}
            onUpdateDetails={updateColumnDetails}
            scrollToItemId={scrollToItemId}
            initialNameFocus={lastNewColumnNameRef.current === option.name}
            // TODO: Change this to something that ensures it's an editable column
            // instead of hard-coded MissingStatusColumn.Id.
            isUserEditable={isEditable}
            isDraggable={isDraggable}
            isCurrentIteration={isVerticalGroupCurrentIteration(option)}
            iterationDateRange={iterationDateRangeMap.get(option.id) ?? ''}
            horizontalGroup={horizontalGroup}
            headerType={headerType}
            isLastGroup={isLastGroup}
            horizontalGroupIndex={horizontalGroupIndex}
          />
        )
      })}
      {memex_table_without_limits && <BoardPagination />}
    </>
  )
}

try{ Columns.displayName ||= 'Columns' } catch {}